import type { ContentfulAssetFragment, Maybe } from "@server/gql/graphql";
import { getImageProps, type ImageProps as NextImageProps } from "next/image";
import type { ComponentPropsWithoutRef } from "react";

import { breakpoints } from "@/config/breakpoints";
import { cn } from "@/lib/utils";

type ContentfulPictureProps = ComponentPropsWithoutRef<"picture"> & {
  imageProps?: ImageProps;
  desktopImage?: Maybe<ContentfulAssetFragment>;
  mobileImage?: Maybe<ContentfulAssetFragment>;
  mobileBreakpoint?: (typeof breakpoints)[keyof typeof breakpoints];
  media?: string;
  // Each source loads only when its query matches, and the img has no src.
  gatedMedia?: {
    mobile?: string;
    desktop?: string;
  };
};

type ImageProps = Omit<NextImageProps, "src" | "alt" | "loader">;

function assetAlt(image?: Maybe<ContentfulAssetFragment>) {
  return image?.description ?? image?.title ?? "";
}

function contentfulImageProps(
  image: ContentfulAssetFragment,
  imageProps: ImageProps,
  alt: string,
) {
  const {
    className: _className,
    priority,
    fetchPriority,
    ...rest
  } = imageProps;

  return getImageProps({
    ...rest,
    alt,
    src: image.url ?? "",
    width: image.width ?? undefined,
    height: image.height ?? undefined,
    sizes: rest.sizes ?? "100vw",
    priority,
    fetchPriority: priority ? "high" : fetchPriority,
  }).props;
}

export default function ContentfulPicture({
  className,
  imageProps = {},
  desktopImage,
  mobileImage,
  mobileBreakpoint = breakpoints.lg,
  media,
  gatedMedia,
  ...props
}: ContentfulPictureProps) {
  const image = desktopImage?.url ? desktopImage : mobileImage;

  if (!image?.url) {
    return null;
  }

  const alt = assetAlt(desktopImage) || assetAlt(mobileImage);
  const { className: imageClassName } = imageProps;
  const imgProps = contentfulImageProps(image, imageProps, alt);

  if (media) {
    return (
      <picture className={cn("relative", className)} {...props}>
        <source
          media={media}
          srcSet={imgProps.srcSet || imgProps.src}
          sizes={imgProps.sizes}
        />
        <img
          alt={alt}
          width={imgProps.width}
          height={imgProps.height}
          fetchPriority={imgProps.fetchPriority}
          className={cn("object-cover", imageClassName)}
        />
      </picture>
    );
  }

  if (gatedMedia) {
    const sources = gatedSources(
      desktopImage,
      mobileImage,
      gatedMedia,
      imageProps,
      alt,
    );

    if (!sources.length) {
      return null;
    }

    const fallback = sources[0];

    return (
      <picture className={cn("relative", className)} {...props}>
        {sources.map((source) => (
          <source
            key={source.media}
            media={source.media}
            srcSet={source.srcSet}
            sizes={source.sizes}
          />
        ))}
        <img
          alt={alt}
          width={fallback.width}
          height={fallback.height}
          fetchPriority={fallback.fetchPriority}
          className={cn("object-cover", imageClassName)}
        />
      </picture>
    );
  }

  const mobileSource =
    mobileImage?.url && mobileImage.url !== image.url
      ? contentfulImageProps(mobileImage, imageProps, alt)
      : undefined;

  return (
    <picture className={cn("relative", className)} {...props}>
      {mobileSource?.srcSet ? (
        <source
          media={`(max-width: ${mobileBreakpoint}px)`}
          srcSet={mobileSource.srcSet}
          sizes={mobileSource.sizes}
        />
      ) : null}
      <img
        {...imgProps}
        className={cn("object-cover", imageClassName)}
        alt={alt}
      />
    </picture>
  );
}

function gatedSources(
  desktopImage: Maybe<ContentfulAssetFragment> | undefined,
  mobileImage: Maybe<ContentfulAssetFragment> | undefined,
  gatedMedia: { mobile?: string; desktop?: string },
  imageProps: ImageProps,
  alt: string,
) {
  const entries: { asset: ContentfulAssetFragment; media: string }[] = [];
  const sameFile =
    Boolean(desktopImage?.url) && desktopImage?.url === mobileImage?.url;

  if (sameFile && desktopImage) {
    const media = [gatedMedia.mobile, gatedMedia.desktop]
      .filter(Boolean)
      .join(", ");
    if (media) entries.push({ asset: desktopImage, media });
  } else {
    if (mobileImage?.url) {
      const media =
        gatedMedia.mobile ??
        (!desktopImage?.url ? gatedMedia.desktop : undefined);
      if (media) entries.push({ asset: mobileImage, media });
    }
    if (desktopImage?.url && desktopImage.url !== mobileImage?.url) {
      const media =
        gatedMedia.desktop ??
        (!mobileImage?.url ? gatedMedia.mobile : undefined);
      if (media) entries.push({ asset: desktopImage, media });
    }
  }

  return entries.flatMap((entry) => {
    const props = contentfulImageProps(entry.asset, imageProps, alt);
    const srcSet = props.srcSet || props.src;
    if (!srcSet) return [];
    return [
      {
        media: entry.media,
        srcSet,
        sizes: props.sizes,
        width: props.width,
        height: props.height,
        fetchPriority: props.fetchPriority,
      },
    ];
  });
}
